import React, { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Stack } from '@atlaskit/primitives';
import { SpotlightTransition } from '@atlaskit/onboarding';

import type {
	ITFGAnalyticsCallbackParams,
	Edition as InTrialFeatureGatesBannerEdition,
} from '@atlassian/in-trial-feature-gates-banner';
import {
	useInTrialFeatureGatesExperimentEnrollmentAB,
	InTrialFeatureGatesBannerWithData,
	FEATURE_ID_USD_MODAL,
	formatEventCallBackPayloadForAtlasKitAnalyticsEvent,
} from '@atlassian/in-trial-feature-gates-banner';

import { getAGGClient } from '@confluence/graphql';
import { NavigationLockout } from '@confluence/navigation-lockout';
import { ConfluenceEdition, useSessionData } from '@confluence/session-data';

import { GeneralAccess, type GeneralAccessProps } from './generalAccess/GeneralAccess';
import { PrincipalsStager, type PrincipalsStagerProps } from './PrincipalsStager';
import { TitleAndButtons, type TitleAndButtonsProps } from './TitleAndButtons';
import { InfoMessage, type InfoMessageProps } from './infoMessage/InfoMessage';
import { SpecificAccess, type SpecificAccessProps } from './specificAccess/SpecificAccess';
import { useHeaderChangeBoarding } from './HeaderChangeboarding';

export type HeaderProps = {
	titleAndButtons: TitleAndButtonsProps;
	generalAccess?: GeneralAccessProps;
	principalsStager?: PrincipalsStagerProps;
	infoMessage?: InfoMessageProps;
	specificAccess?: SpecificAccessProps;
	hasNavigationLockout: boolean;
	isSpotlightActive: boolean;
	// showDraftMessage: boolean; // TODO
};

export const Header = ({
	titleAndButtons,
	generalAccess,
	specificAccess,
	principalsStager,
	infoMessage,
	hasNavigationLockout,
	isSpotlightActive,
	// showDraftMessage,
}: HeaderProps) => {
	const { cloudId, edition } = useSessionData();
	const aggClient: any = getAGGClient();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const inTrialFeatureGatesAnalyticsCallback = useCallback(
		(params: ITFGAnalyticsCallbackParams) => {
			createAnalyticsEvent(formatEventCallBackPayloadForAtlasKitAnalyticsEvent(params)).fire();
		},
		[createAnalyticsEvent],
	);
	// AB hooks for in trial feature gates experiment - https://hello.atlassian.net/wiki/spaces/Land/pages/4343595130/Project+poster+Confluence+Feature+Gate+Add+Payment+Prompts
	const { canRenderTreatment } = useInTrialFeatureGatesExperimentEnrollmentAB({
		featureId: FEATURE_ID_USD_MODAL,
		tenantId: cloudId,
		sendOperationalEvent: inTrialFeatureGatesAnalyticsCallback,
		sendTrackEvent: inTrialFeatureGatesAnalyticsCallback,
		aggClient,
		skip: edition === ConfluenceEdition.FREE,
		extraAnalyticsAttributes: {
			edition: edition?.toLowerCase(),
		},
	});

	const { renderActiveSpotlight } = useHeaderChangeBoarding(isSpotlightActive);

	return (
		<Stack grow="fill" space="space.200">
			<TitleAndButtons {...titleAndButtons} />
			{principalsStager && <PrincipalsStager {...principalsStager} />}
			{infoMessage && <InfoMessage {...infoMessage} />}
			{canRenderTreatment && edition && (
				<InTrialFeatureGatesBannerWithData
					featureId={FEATURE_ID_USD_MODAL}
					aggClient={aggClient}
					edition={edition.toLowerCase() as InTrialFeatureGatesBannerEdition}
					tenantId={cloudId}
					sendOperationalEvent={inTrialFeatureGatesAnalyticsCallback}
					sendTrackEvent={inTrialFeatureGatesAnalyticsCallback}
					sendScreenEvent={inTrialFeatureGatesAnalyticsCallback}
				/>
			)}
			{generalAccess && (
				<GeneralAccess {...generalAccess} showBottomPadding={!Boolean(specificAccess)} />
			)}
			{specificAccess && <SpecificAccess {...specificAccess} />}
			<SpotlightTransition>{renderActiveSpotlight()}</SpotlightTransition>
			{hasNavigationLockout && <NavigationLockout />}
		</Stack>
	);
};
